const AUTH_ERROR_CODES = new Set([
  "TOKEN_EXPIRED",
  "INVALID_TOKEN",
  "UNAUTHENTICATED",
  "UNAUTHORIZED",
  "UNVERIFIED_JWT_TOKEN",
]);

const PRODUCT_NOT_FOUND_CODES = new Set([
  "PRODUCT_NOT_FOUND",
  "RECORD_NOT_FOUND",
  "NOT_FOUND",
]);

type GraphQLLikeError = {
  message?: string;
  code?: string;
  extensions?: { code?: string };
};

function getErrorCode(error: GraphQLLikeError): string {
  return String(error?.extensions?.code || error?.code || "").toUpperCase();
}

function matchesAuthMessage(message: string): boolean {
  const normalized = message.toLowerCase();
  return (
    normalized.includes("unverified_jwt_token") ||
    normalized.includes("token_expired") ||
    normalized.includes("invalid_token") ||
    normalized.includes("jwt expired") ||
    normalized.includes("access-denied") ||
    normalized.includes("unauthenticated")
  );
}

export function isAuthGraphQLError(error: GraphQLLikeError): boolean {
  const code = getErrorCode(error);
  if (AUTH_ERROR_CODES.has(code)) return true;
  const message = String(error?.message || "");
  if (AUTH_ERROR_CODES.has(message.toUpperCase())) return true;
  return matchesAuthMessage(message);
}

export function isProductNotFoundError(error: unknown): boolean {
  const graphQLErrors = getGraphQLErrors(error);
  if (
    graphQLErrors.some((err) => PRODUCT_NOT_FOUND_CODES.has(getErrorCode(err)))
  ) {
    return true;
  }
  if (
    graphQLErrors.some((err) =>
      PRODUCT_NOT_FOUND_CODES.has(String(err.message || "").toUpperCase())
    )
  ) {
    return true;
  }
  return false;
}

function getGraphQLErrors(error: unknown): GraphQLLikeError[] {
  if (!error || typeof error !== "object") return [];
  const maybe = error as {
    graphQLErrors?: GraphQLLikeError[];
    message?: string;
    extensions?: { code?: string };
    code?: string;
  };
  if (Array.isArray(maybe.graphQLErrors) && maybe.graphQLErrors.length) {
    return maybe.graphQLErrors;
  }
  if (maybe.message || maybe.extensions || maybe.code) {
    return [maybe];
  }
  return [];
}

function getNetworkStatus(error: unknown): number | undefined {
  if (!error || typeof error !== "object") return undefined;
  const maybe = error as {
    networkError?: { statusCode?: number; status?: number };
    statusCode?: number;
    status?: number;
  };
  return (
    maybe.networkError?.statusCode ??
    maybe.networkError?.status ??
    maybe.statusCode ??
    maybe.status
  );
}

export function isAuthenticationError(error: unknown): boolean {
  if (!error) return false;
  if (getGraphQLErrors(error).some(isAuthGraphQLError)) return true;
  return getNetworkStatus(error) === 401;
}

export function shouldRetryWithoutAuth(params: {
  alreadyRetried: boolean;
  hadAuthorization: boolean;
  graphQLErrors?: readonly GraphQLLikeError[] | null;
  networkError?: { statusCode?: number; status?: number } | Error | null;
}): boolean {
  if (params.alreadyRetried || !params.hadAuthorization) return false;
  if (params.graphQLErrors?.some(isAuthGraphQLError)) return true;
  const status =
    params.networkError && typeof params.networkError === "object"
      ? ((params.networkError as { statusCode?: number; status?: number })
          .statusCode ?? (params.networkError as { status?: number }).status)
      : undefined;
  return status === 401;
}
